/* Journal topic hub listing.
 *
 * <journal-hub-list> holds skeleton cards server-rendered by
 * sections/journal-hub.liquid. It fetches the hub's tagged route with
 * ?view=hublist (templates/blog.hublist.liquid, rendered without a layout),
 * swaps the real cards in, and appends a page at a time from the same endpoint
 * when the reader asks for more. The fragment carries the total count and
 * whether another page exists, so the hub never has to guess.
 *
 * Any failure degrades to a plain link to the tagged route, which lists the
 * same articles with Shopify's own pagination.
 */
(function () {
  'use strict';

  if (customElements.get('journal-hub-list')) return;

  class JournalHubList extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;

      this.grid = this.querySelector('[data-hub-cards]');
      this.moreButton = this.querySelector('[data-hub-more]');
      this.countEl = this.closest('.fbjm-hub__listing')
        ? this.closest('.fbjm-hub__listing').querySelector('[data-hub-count]')
        : null;
      this.page = 1;
      this.moreLabel = this.moreButton ? this.moreButton.textContent.trim() : 'Load more';

      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => this._loadMore());
      }
      this._load(1, false);
    }

    async _fetchPage(page) {
      const url = this.dataset.src + (page > 1 ? '&page=' + page : '');
      const response = await fetch(url);
      if (!response.ok) throw new Error('hub fetch ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fragment = doc.querySelector('[data-journal-hub-fragment]');
      if (!fragment) throw new Error('hub fragment missing');
      return {
        cards: fragment.querySelectorAll('.fbjm-card'),
        total: parseInt(fragment.dataset.total, 10) || 0,
        hasNext: fragment.dataset.hasNext === 'true',
      };
    }

    async _load(page, append) {
      if (!this.grid || !this.dataset.src) return;
      try {
        const { cards, total, hasNext } = await this._fetchPage(page);
        if (!cards.length && !append) {
          this._fallback();
          return;
        }
        const firstNew = cards[0];
        if (append) {
          this.grid.append(...cards);
        } else {
          this.grid.replaceChildren(...cards);
          this.grid.removeAttribute('aria-busy');
          this._setCount(total);
        }
        this.page = page;
        this._setMore(hasNext);
        if (append && firstNew) {
          const link = firstNew.querySelector('a');
          if (link) {
            link.setAttribute('tabindex', '-1');
            link.focus({ preventScroll: true });
          }
        }
      } catch (error) {
        if (append) {
          this._setMore(false);
          this._appendFallbackLink();
        } else {
          this._fallback();
        }
      }
    }

    _loadMore() {
      if (!this.moreButton || this.moreButton.disabled) return;
      this.moreButton.disabled = true;
      this.moreButton.textContent = this.moreButton.dataset.loadingLabel || 'Loading';
      this._load(this.page + 1, true).then(() => {
        this.moreButton.disabled = false;
        this.moreButton.textContent = this.moreLabel;
      });
    }

    _setMore(hasNext) {
      if (!this.moreButton) return;
      this.moreButton.hidden = !hasNext;
    }

    _setCount(total) {
      if (!this.countEl || !total) return;
      const one = this.dataset.countOne || '1 article';
      const many = this.dataset.countMany || '[count] articles';
      this.countEl.textContent = total === 1 ? one : many.replace('[count]', total);
    }

    _appendFallbackLink() {
      const wrapper = this.querySelector('.fbjm-hub__actions');
      if (!wrapper || wrapper.querySelector('.fbjm-hub__fallback')) return;
      wrapper.appendChild(this._fallbackLink('fbjm-go fbjm-hub__fallback'));
    }

    _fallbackLink(className) {
      const link = document.createElement('a');
      link.className = className;
      link.href = this.dataset.fallbackHref || this.dataset.src.split('?')[0];
      link.textContent = this.dataset.fallbackLabel || 'View every article on this topic';
      return link;
    }

    _fallback() {
      this.grid.replaceChildren(this._fallbackLink('fbjm-go fbjm-rail__fallback'));
      this.grid.removeAttribute('aria-busy');
      this._setMore(false);
    }
  }

  customElements.define('journal-hub-list', JournalHubList);
})();
